import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  computed,
  inject,
} from '@angular/core'
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalData,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { SystemSummary } from '@flowx/designer/modules/integration-designer/models/system.entity'
import { ITaxonomyValue } from '@flowx/designer/shared/api/taxonomy.api'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'
import { EnterSubmitFormDirective } from '@flowx/designer/shared/directives/enter-submit-form.directive'
import { ContentValue } from '@flowx/designer/shared/entities/enumeration.entity'
import { KeyParametersRegex } from '@flowx/designer/shared/entities/utils'
import {
  RESERVED_SYSTEM_TAG_PREFIX,
  reservedSystemTagPrefixValidator,
} from '@flowx/designer/shared/validators'

@Component({
  selector: 'flx-content-value-edit',
  imports: [
    FlxModalModule,
    FlxSpinnerModule,
    FlxFormControlModule,
    ReactiveFormsModule,
    FlxButtonModule,
    EnterSubmitFormDirective,
    FlxAutofocusDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './content-value-edit.component.html',
})
export class FlxContentValueEditComponent implements OnInit {
  #modalService = inject(FlxModalService)
  #formBuilder = inject(UntypedFormBuilder)
  @Output() dataEventEmitter = new EventEmitter<Partial<ContentValue>>()
  data: FlxModalData = inject(FLX_MODAL_DATA)
  isNew = this.data?.isNew
  isEnumeration = this.data?.type === 'enumeration'
  readOnly = this.data?.readOnly

  languages: ITaxonomyValue[] = this.data.languages
  sourceSystemsValues: SystemSummary[] = this.data.sourceSystems
  currentMaxOrder: number = this.data.currentMaxOrder || 0

  modalTitle = computed(() => {
    const suffix = this.isEnumeration ? 'enumeration' : 'substitution tag'

    if (this.readOnly) {
      return `View ${suffix}`
    }

    if (!this.isNew) {
      return `Edit ${suffix}`
    }

    return `Create ${suffix}`
  })

  placeholder = computed(() => (this.isEnumeration ? 'Enumeration code' : 'Substitution tag key'))

  keyTitle = this.isEnumeration ? 'Code' : 'Key'
  keyError = `${this.isEnumeration ? 'Code' : 'Key'} is required`
  submitButtonTitle = this.isNew ? 'Add new' : 'Update'

  reservedPrefixError = `Prefix ${RESERVED_SYSTEM_TAG_PREFIX} is reserved for system substitution tags.`

  codeFC = new FormControl<string>(null, [
    Validators.required,
    Validators.pattern(KeyParametersRegex),
    ...(this.isEnumeration ? [] : [reservedSystemTagPrefixValidator]),
  ])

  orderFC = new FormControl<number>(null, [Validators.required, Validators.min(0)])

  newContentValueForm = this.#formBuilder.group({
    code: this.codeFC,
    valueId: null,
    contentMap: this.#formBuilder.group({}),
  })

  ngOnInit(): void {
    this.languages?.forEach((value) => {
      this.contentMap.addControl(value.code, new FormControl(null))
    })
    if (this.isEnumeration) {
      this.newContentValueForm.addControl('sourceSystems', this.#formBuilder.group({}))
      this.sourceSystemsValues?.forEach((value) => {
        this.sourceSystems.addControl(value.code, new FormControl(null))
      })

      // Add order control for enumerations
      this.newContentValueForm.addControl('order', this.orderFC)

      // Set default value for order
      if (this.isNew) {
        // For new content, set order to current maximum + 1
        this.orderFC.setValue(this.currentMaxOrder + 1)
      } else {
        // For edit, use existing order or default to 1
        const currentOrder = this.data.contentValue?.order || 1
        this.orderFC.setValue(currentOrder)
      }
    }
    if (!this.isNew) {
      const contentValue = this.data.contentValue
      this.newContentValueForm.patchValue(this.data.contentValue)

      if (contentValue?.systemResource === true) {
        this.newContentValueForm.controls.code.disable()
      }
    }

    if (this.readOnly) {
      this.newContentValueForm.disable()
    }
  }

  get contentMap(): UntypedFormGroup {
    return this.newContentValueForm.get('contentMap') as UntypedFormGroup
  }

  get sourceSystems(): UntypedFormGroup {
    return this.newContentValueForm.get('sourceSystems') as UntypedFormGroup
  }

  submitModal(): void {
    const formValue = this.newContentValueForm.getRawValue()

    if (formValue.code) {
      // Convert empty strings to null for contentMap
      if (formValue.contentMap) {
        Object.keys(formValue.contentMap).forEach((key) => {
          if (formValue.contentMap[key] === '') {
            formValue.contentMap[key] = null
          }
        })
      }

      // Convert empty strings to null for sourceSystems
      if (formValue.sourceSystems) {
        Object.keys(formValue.sourceSystems).forEach((key) => {
          if (formValue.sourceSystems[key] === '') {
            formValue.sourceSystems[key] = null
          }
        })
      }

      this.dataEventEmitter.emit(formValue)
    }
  }

  closeModal(): void {
    this.#modalService.hide()
  }
}
